import React, { Component, ErrorInfo, useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { deleteError, getStore as getErrorsStore } from '~/store/reducers/errors/errors';
import { useTranslation } from '@sunbit-dev/translations';
import { ErrorBoundariesContainerProps, ErrorBoundariesProps } from './models';
import { ErrorModal } from '../errorModal/ErrorModal';
import * as styles from './styles';
import { useTrack } from '~/hooks/track';
import { TrackCategory } from '@sunbit-dev/purchase-common/src/services/networkService/models';
import { getErrorModalDataTestId } from '@sunbit-dev/purchase-common/src/utilities/errors';
import { ErrorCodes } from '~/enums/Errors';
import { errorsMetaData } from './constants';
import { EventsNames as CommonEventNames } from '@sunbit-dev/purchase-common/src/enums/Events';
import { useHistory, useLocation } from 'react-router-dom';
import CancellationReasons from '~/enums/CancellationReasons';
import URLs, { MessageURLs } from '~/enums/URLs';

class ErrorBoundary extends Component<ErrorBoundariesProps> {
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { track } = this.props;
    track({
      eventName: CommonEventNames.componentError,
      datadogInfo: { type: TrackCategory.Error },
      props: { errorInfo, error },
    });
  }

  render() {
    const { className, modalError, children, onClose } = this.props;
    const { metaData, id: errorId, payload } = modalError || {};

    return (
      <div className={classNames(className)}>
        {!!modalError && (
          <ErrorModal
            onClose={onClose}
            texts={metaData?.texts}
            id={errorId}
            dataTestId={getErrorModalDataTestId(payload?.code)}
          />
        )}
        {children}
      </div>
    );
  }
}

export const ErrorBoundaryContainer = styled(({ className, children }: ErrorBoundariesContainerProps) => {
  const { errors } = useSelector(getErrorsStore);
  const dispatch = useDispatch();
  const history = useHistory();

  const { t } = useTranslation('errors');
  const { track } = useTrack();
  const { search } = useLocation();

  const onClose = useCallback((id: string) => {
    dispatch(deleteError(id));
  }, []);

  const {
    defaultError,
    cancelApplicationPosition,
    technicalErrorPosition,
    expiredLinkErrorPosition,
    sessionExpiredErrorPosition,
  } = useMemo(() => {
    let cancelApplicationPosition = undefined;
    let technicalErrorPosition = undefined;
    let expiredLinkErrorPosition = undefined;
    let sessionExpiredErrorPosition = undefined;
    let defaultError;

    for (let position = 1; position < errors.length + 1; position++) {
      const index = position - 1;
      const originalErrorCode = errors[index]?.payload?.code;

      if (originalErrorCode === ErrorCodes.ExpiredLink || errors[index]?.payload?.status === 401) {
        expiredLinkErrorPosition = position;
        break;
      }
      if (originalErrorCode === ErrorCodes.WrongStateOfPurchase) {
        sessionExpiredErrorPosition = position;
        break;
      }

      const { textsKey, cancelApplication, cancellationReason, showRefreshButton } =
        (originalErrorCode && errorsMetaData[originalErrorCode]) || errorsMetaData[ErrorCodes.FallbackError];

      if (position === 1) {
        defaultError = {
          ...errors[index],
          metaData: {
            texts: t(textsKey),
            showRefreshButton,
          },
        };
      }

      if (cancelApplication) {
        cancelApplicationPosition = position;
        break;
      }
      if (cancellationReason === CancellationReasons.TechnicalIssue) technicalErrorPosition = position;
    }

    return {
      defaultError,
      cancelApplicationPosition,
      technicalErrorPosition,
      expiredLinkErrorPosition,
      sessionExpiredErrorPosition,
    };
  }, [errors]);

  useEffect(() => {
    if (cancelApplicationPosition) {
      history.replace({
        pathname: `${URLs.Decline}/${btoa(errors[cancelApplicationPosition - 1]?.payload?.code)}`,
        search,
      });
    }
  }, [cancelApplicationPosition]);

  useEffect(() => {
    if (expiredLinkErrorPosition) {
      history.replace({ pathname: URLs.LinkExpired, search });
    }
  }, [expiredLinkErrorPosition]);

  useEffect(() => {
    if (sessionExpiredErrorPosition) {
      history.replace({ pathname: URLs.Message + MessageURLs.SessionExpiredByUI, search });
    }
  }, [sessionExpiredErrorPosition]);

  useEffect(() => {
    if (technicalErrorPosition) {
      history.replace({ pathname: URLs.Message + MessageURLs.TechnicalError, search });
    }
  }, [technicalErrorPosition]);

  const showErrorModal =
    defaultError && !technicalErrorPosition && !cancelApplicationPosition && !expiredLinkErrorPosition;

  return (
    <ErrorBoundary className={className} modalError={showErrorModal && defaultError} onClose={onClose} track={track}>
      {children}
    </ErrorBoundary>
  );
})`
  ${styles.ErrorBoundary}
`;
